import React, {useEffect, useRef, useState} from 'react';
import {DatePicker, DatePickerProps, Form, Input, message} from 'antd';
import {ProFormInstance} from '@ant-design/pro-form';
import {wdiCore, WdiInput, WdiModalAppProps} from '@/components/WdiControls';
import {ResponseStatus} from '@/utils/request';
import {Invoices} from '@/services/sales/financial/invoices';
import {HorzLayoutOpts, WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';
import moment from 'moment';
import {Supplier} from '@/services/mdm/supplier';
import {Enum} from '@/enums/system';
import dayjs from 'dayjs';
import {SoldTo} from '@/services/mdm/soldto';
import {Company} from "@/services/mdm/company";
import {Staff} from "@/services/basic/staff";

const FormInvoiceEditApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [loading, setLoading] = useState(false);
  const {data} = props;

  const {paymentMethodId, vatType, soldToCode, supplierId, payToUserId, companyCode} = data;

  const disabledDate: DatePickerProps['disabledDate'] = (current) => {
    return current && current > dayjs().endOf('day');
  };

  useEffect(() => {
    if (soldToCode) {
      SoldTo.get(soldToCode).then((responseResult) => {
        formRef.current?.setFieldsValue({
          soldToCode: responseResult.data.codeOrName,
        });
      });
    }
    if (supplierId) {
      Supplier.get(supplierId).then((responseResult) => {
        formRef.current?.setFieldsValue({
          supplier: responseResult.data.codeOrName,
        });
      });
    }
    if ((paymentMethodId == Enum.PaymentMethod.THIRD_PART || paymentMethodId == Enum.PaymentMethod.TRANSFER) && companyCode) {
      Company.get(companyCode).then((responseResult) => {
        formRef.current?.setFieldsValue({
          salesOrgCode: responseResult.data.salesOrgCode,
        });
      });
    }
    if (payToUserId) {
      Staff.get(payToUserId).then((responseResult) => {
        formRef.current?.setFieldsValue({
          payToUser: wdiCore.String.GenerateLabelDescription(responseResult.data?.staffNoOrName, responseResult.data?.title),
        });
      });
    }
    formRef.current?.setFieldsValue({
      vatType: wdiCore.Number.RateToString(vatType?.vatTypeValue),
    });
  }, [data]);

  return (
    <>
      <WdiModalForm
        formRef={formRef}
        {...HorzLayoutOpts}
        title={props.title}
        visible={props.visible}
        modalProps={{
          onCancel: () => {
            if (!loading) {
              props.onClose?.();
            }
          },
          cancelButtonProps: {hidden: true},
          okButtonProps: {loading: loading},
          okText: '保存',
        }}
        onFinish={async (values) => {
          const {invoiceDate, ...entity} = values;
          if (entity.amountExVat <= 0) {
            message.error('不含税金额必须大于0');
            return;
          }
          setLoading(true);
          entity.invoiceDate = moment(invoiceDate).format('YYYY-MM-DD');
          entity.supplierId = supplierId;
          entity.soldToCode = soldToCode;
          entity.payToUserId = payToUserId;
          entity.invoiceSource = Enum.InvoiceSource.CREATE;
          entity.taxRate = vatType?.vatTypeValue;
          const response = await Invoices.post({invoices: [entity]});
          setLoading(false);
          if (response.code != ResponseStatus.SUCCESS) {
            message.error(response.message);
          } else {
            props.onSave?.({data: response.data[0], code: ResponseStatus.SUCCESS});
          }
        }}
      >
        <Form.Item
          name="invoiceNo"
          label="发票号码"
          rules={[{required: true, message: '发票号码不能为空!'}]}
        >
          <Input/>
        </Form.Item>
        <Form.Item name="invoiceCode" label="发票代码">
          <Input/>
        </Form.Item>

        <Form.Item
          name="invoiceDate"
          label="发票日期"
          rules={[{required: true, message: '发票日期不能为空!'}]}
        >
          <DatePicker picker={'date'} format={'YYYY-MM-DD'} disabledDate={disabledDate}/>
        </Form.Item>
        <Form.Item
          name="vatType"
          label="税率"

        >
          <Input readOnly/>
        </Form.Item>
        <Form.Item
          name="soldToCode"
          label="客户"
          hidden={
            paymentMethodId != Enum.PaymentMethod.ACCOUNT_DEDUCT && paymentMethodId != Enum.PaymentMethod.TRANSFER
          }
        >
          <WdiInput readOnly/>
        </Form.Item>
        <Form.Item
          name="supplier"
          label="供应商"
          hidden={paymentMethodId != Enum.PaymentMethod.THIRD_PART || wdiCore.Object.IsNullOrUndefined(supplierId)}
        >
          <WdiInput readOnly/>
        </Form.Item>
        <Form.Item
          name="payToUser"
          label="员工"
          hidden={paymentMethodId != Enum.PaymentMethod.THIRD_PART || wdiCore.Object.IsNullOrUndefined(payToUserId)}
        >
          <WdiInput readOnly/>
        </Form.Item>

        <Form.Item name="salesOrgCode" label="salesOrgCode" hidden>
          <Input/>
        </Form.Item>
        <Form.Item
          name="amountExVat"
          label="不含税金额"
          rules={[{required: true, message: '不含税金额不能为空!'}]}
        >
          <WdiInputNumber
            min={0}
            onBlur={(e: any) => {
              const amountExVat = wdiCore.Number.ToNumber(e.target.value, 2);
              formRef.current?.setFieldsValue({amountExVat: amountExVat});
              if (!wdiCore.Object.IsNullOrUndefined(vatType.vatTypeValue)) {
                const tax = wdiCore.Number.ToNumber(vatType.vatTypeValue * amountExVat, 2);
                const amountInVat = wdiCore.Number.ToNumber(amountExVat + tax, 2);
                formRef.current?.setFieldsValue({
                  amountInVat: amountInVat,
                  tax: tax,
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item name="tax" label="税金" rules={[{required: true, message: '税金不能为空!'}]}>
          <WdiInputNumber
            min={0}
            onBlur={(e: any) => {
              const amountExVat = formRef.current?.getFieldValue('amountExVat') || 0;
              const tax = wdiCore.Number.ToNumber(e.target.value, 2);
              const amountInVat = wdiCore.Number.ToNumber(amountExVat + tax, 2);
              formRef.current?.setFieldsValue({
                amountInVat: amountInVat,
              });
            }}
          />
        </Form.Item>
        <Form.Item
          name="amountInVat"
          label="发票总金额"
          rules={[{required: true, message: '发票总金额不能为空!'}]}
        >
          <WdiInputNumber readOnly/>
        </Form.Item>
        <Form.Item name="remark" label="备注">
          <Input.TextArea style={{resize: 'both'}} maxLength={2000}/>
        </Form.Item>
      </WdiModalForm>
    </>
  );
};
export default FormInvoiceEditApp;
